import {
  type CategoryTypes,
  type ChatPrompt,
  type IconName,
} from './chat-prompt-library.types';

export const CATEGORY_ICONS: Record<CategoryTypes, IconName> = {
  Reflect: 'history',
  Briefings: 'summary',
  Drafts: 'write',
  Actions: 'fast',
  People: 'attendees',
  Life: 'journal',
};

const stableHash = (input: string): number => {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Starter prompts for the chat library modal.
//
// Governing test: every prompt here should be meaningfully worse coming from
// an AI that hasn't been watching your week.
export const CHAT_PROMPTS: ChatPrompt[] = [
  // --- Reflect ---
  // The signature Littlebird move: honest self-audit against real behavior.
  {
    id: 'reflect-time-allocation',
    category: 'Reflect',
    question: 'Am I spending my time on the right things?',
    teaser:
      "A look at where your week actually went vs what you've said matters.",
    prompt:
      "Compare where my week actually went to what I've said matters. Be honest with me — where's the gap, and where am I lying to myself about being busy?",
  },
  {
    id: 'reflect-quietly-slipping',
    category: 'Reflect',
    question: "What's quietly slipping?",
    teaser: "Threads, commitments, and tasks you've been quietly skipping.",
    prompt:
      "What have I been putting off for weeks that I shouldn't be? Stale threads, unstarted commitments, the stuff I keep quietly skipping past.",
  },
  {
    id: 'reflect-kill-meetings',
    category: 'Reflect',
    question: 'Which recurring meetings can I kill?',
    teaser: "Recurring meetings that aren't earning their spot anymore.",
    prompt:
      "Look at my recurring meetings. Which ones aren't earning their spot on my calendar anymore — and how would I gracefully get out of them?",
  },
  {
    id: 'reflect-said-yes-to',
    category: 'Reflect',
    question: 'What did I say yes to?',
    teaser: "Everything you've committed to lately and what's still open.",
    prompt:
      "Pull everything I've committed to or said yes to in the last two weeks — in meetings, Slack, email, wherever. Which have I actually started? Which are still open?",
  },
  {
    id: 'reflect-hidden-pattern',
    category: 'Reflect',
    question: "Tell me a pattern I wouldn't notice",
    teaser: "A pattern in how you work that you wouldn't notice yourself.",
    prompt:
      'Surface a pattern about how I work that I probably would not notice myself. When do I do my best work? What tends to throw me off? What do my rough weeks have in common?',
  },
  {
    id: 'reflect-follow-through',
    category: 'Reflect',
    question: 'Did I follow through?',
    teaser: 'An honest read on whether you delivered what you said you would.',
    prompt:
      "Pick a recent call — ask me which — and tell me whether I actually followed through on what I said I would. Don't be gentle about it.",
  },

  // --- Briefings ---
  // Forward-looking context. The "get me ready for what's next" category.
  {
    id: 'briefings-morning',
    category: 'Briefings',
    question: 'Morning Brief',
    teaser:
      "Today's priorities, meetings to prep for, and what you might drop.",
    prompt:
      "Give me my morning brief — what's on today, the priorities I should actually care about, meetings worth prepping for, and anything I'm at risk of dropping.",
  },
  {
    id: 'briefings-end-of-day',
    category: 'Briefings',
    question: 'End of Day Wrap-Up',
    teaser:
      "What got done, what's still open, what to close before signing off.",
    prompt:
      "Wrap up my day. What got done, what's still open, and what I should close out or reply to before I sign off.",
  },
  {
    id: 'briefings-walking-into',
    category: 'Briefings',
    question: 'What am I walking into?',
    teaser:
      'Context for the rest of your day so nothing catches you off guard.',
    prompt:
      "Look at the rest of my day. What should I know walking in — context for upcoming meetings, things on my mind, anything I'd kick myself for forgetting.",
  },
  {
    id: 'briefings-one-on-one-prep',
    category: 'Briefings',
    question: '1:1 Prep',
    teaser: "What you've been working on and what's worth raising next.",
    prompt:
      "Prep me for a 1:1. Ask who it's with, then pull what I've actually been working on since we last talked, real wins worth raising, and what I want to push on.",
  },
  {
    id: 'briefings-project-state',
    category: 'Briefings',
    question: "What's going on with my projects?",
    teaser: "The real state of each active project — what moved, what's stuck.",
    prompt:
      "For each of my active projects, pull from meetings, Slack, docs, and my own notes to tell me the real state — what moved, what's stuck, what I'm not seeing.",
  },

  // --- Drafts ---
  // Produce an artifact in my actual voice, grounded in real activity.
  // These stop at the text — user reviews and decides what to do with it.
  {
    id: 'drafts-status-update',
    category: 'Drafts',
    question: 'Status update for my team',
    teaser: 'A status update in your voice, from what you actually did.',
    prompt:
      "Draft a status update for my team. Pull from what I've actually been doing this week — not what I'd remember off the top of my head. Sound like me.",
  },
  {
    id: 'drafts-meeting-followup',
    category: 'Drafts',
    question: 'Follow-up email from a meeting',
    teaser: 'A meeting follow-up — decisions, next steps, written like you.',
    prompt:
      'Draft the follow-up from a recent meeting in my voice — decisions, next steps with owners, anything worth restating. Ask me which meeting.',
  },
  {
    id: 'drafts-meeting-summary',
    category: 'Drafts',
    question: 'Meeting summary with action items',
    teaser: 'Decisions, takeaways, and action items from a recent meeting.',
    prompt:
      'Summarize a meeting for me — decisions, takeaways, and action items with owners. Ask me which one.',
  },
  {
    id: 'drafts-reply-in-voice',
    category: 'Drafts',
    question: 'Reply in my voice',
    teaser: 'A reply drafted the way you actually write.',
    prompt:
      "Help me write a reply. Ask what it's to and what I want to say — then draft it the way I actually write, based on how you've seen me write before.",
  },

  // --- Actions ---
  // Integrations doing real work in the world: triage, scheduling, nudging.
  // These cross from "here's a draft" into "it's handled."
  {
    id: 'actions-triage-inbox',
    category: 'Actions',
    question: 'Triage my inbox',
    teaser: 'Sort unread email by what matters, with quick replies drafted.',
    prompt:
      "Go through my unread email. Sort it by what actually matters — people I care about, threads I'm blocking, things on fire — and tell me what each one needs from me. Draft quick replies for the easy ones.",
  },
  {
    id: 'actions-find-meeting-time',
    category: 'Actions',
    question: 'Find a time that works',
    teaser: 'A slot that works across everyone, plus a draft invite ready.',
    prompt:
      'Help me schedule a meeting. Ask me who it needs to include and roughly when, then find a time that works across everyone and draft the invite.',
  },
  {
    id: 'actions-nudge-stalled',
    category: 'Actions',
    question: 'Nudge my stalled threads',
    teaser: 'Conversations gone quiet — short, on-voice nudges for each.',
    prompt:
      "Which of my conversations or commitments have gone quiet and shouldn't have? Draft a short nudge for each in my voice — something I'd actually send.",
  },
  {
    id: 'actions-meeting-followups',
    category: 'Actions',
    question: 'Follow up with everyone from a meeting',
    teaser: 'A personalized follow-up to each attendee, in your voice.',
    prompt:
      'From my last meeting — ask me which — draft a personalized follow-up to each attendee, matched to what they specifically seemed to care about.',
  },
  {
    id: 'actions-clear-inbox-noise',
    category: 'Actions',
    question: 'Clear the noise from my inbox',
    teaser: 'Recurring emails you never open — drafted unsubscribes, ready.',
    prompt:
      'What recurring emails am I never actually opening? List them and draft the unsubscribes so I can send them in one pass.',
  },

  // --- People ---
  // Relationship intelligence
  {
    id: 'people-neglecting',
    category: 'People',
    question: 'Who am I neglecting?',
    teaser: "People you care about that you haven't talked to in a while.",
    prompt:
      "Who in my life haven't I talked to in a while that I actually care about — not notifications, the people. Suggest one or two worth reaching out to this week.",
  },
  {
    id: 'people-introduce',
    category: 'People',
    question: 'Who should I connect with each other?',
    teaser: 'Two people in your network who probably should meet.',
    prompt:
      'Look at the people I know. Are there two who should probably meet? Tell me who, and why them specifically.',
  },
  {
    id: 'people-orbit',
    category: 'People',
    question: 'Who keeps coming up in my orbit?',
    teaser: "People showing up around you that you don't really know yet.",
    prompt:
      "Who's been showing up in my meetings, threads, and calendar a lot lately that I don't actually have a real relationship with yet? Worth getting coffee with any of them?",
  },
  {
    id: 'people-make-plans',
    category: 'People',
    question: 'Who should I make plans with this month?',
    teaser: 'Someone you light up around, and a reason to make plans.',
    prompt:
      "Based on who I've been talking to, who I haven't, and who I genuinely seem to light up around — pick one or two people I should make plans with.",
  },
  {
    id: 'people-hard-conversation',
    category: 'People',
    question: 'Prep me for a hard conversation',
    teaser: 'Where things stand and how this person tends to react.',
    prompt:
      "I've got a conversation I've been putting off. Ask me who it's with, then remind me where we actually left things, what's open between us, and how they tend to react when pushed on.",
  },
  {
    id: 'people-honest-feedback',
    category: 'People',
    question: 'Give someone honest feedback',
    teaser:
      "Honest, useful feedback grounded in what you've actually observed.",
    prompt:
      "Help me give a colleague feedback that's honest and actually useful. Ask me who it's for, then pull on what I've actually observed working with them.",
  },

  // --- Life ---
  // I'm a whole person. Includes recommendation prompts — generic chatbots
  // give you "top 10 feel-good movies"; Littlebird can factor in that your
  // week was brutal, you have 90 minutes before bed, and you've already
  // watched the obvious picks.
  {
    id: 'life-teach-me',
    category: 'Life',
    question: "Teach me something based on what I've been into",
    teaser:
      'The deeper version of something showing up in what you read and watch.',
    prompt:
      "Pick something that keeps showing up in what I read and watch. Teach me the version I don't already know — not surface-level.",
  },
  {
    id: 'life-circling',
    category: 'Life',
    question: "Something I've been circling",
    teaser: "Something you keep coming back to — let's actually start it.",
    prompt:
      "What's something I've been circling but haven't started — a book, a trip, a habit, an idea I keep coming back to? Pick one and help me actually begin.",
  },
  {
    id: 'life-attention',
    category: 'Life',
    question: "What's trying to get my attention?",
    teaser: 'The idea or tension showing up across what you read and write.',
    prompt:
      'What idea, topic, or tension keeps showing up in what I read, write, and talk about lately? What might it be trying to tell me?',
  },
  {
    id: 'life-song',
    category: 'Life',
    question: 'A song for right now',
    teaser: 'One song that fits where you are today.',
    prompt:
      "Pick one song that fits what I've been doing today and the mood I'm probably in. Tell me why.",
  },
  {
    id: 'life-watch-tonight',
    category: 'Life',
    question: 'Something to watch tonight',
    teaser: 'One pick that fits your taste, your week, and the time you have.',
    prompt:
      'Recommend one movie or episode for tonight. Factor in my taste, how heavy my week has been, and how much time I actually have before I should sleep. Give me one pick, not a list.',
  },
  {
    id: 'life-weekend-watch',
    category: 'Life',
    question: 'Plan my weekend watch',
    teaser: 'A weekend watch plan based on your mood and your calendar.',
    prompt:
      "Plan my weekend watch. Consider what's on my calendar, what I've been into lately, and whether I'm in the mood for something light or something with substance.",
  },
  {
    id: 'life-cook-tonight',
    category: 'Life',
    question: 'What should I cook tonight?',
    teaser:
      "Tonight's dinner, factoring in who you're with and what you've had.",
    prompt:
      "Suggest dinner for tonight. Consider who I'm eating with, what I've had this week already, and how much time I actually have to cook. Give me one pick, not a list.",
  },
];

export const CHAT_PROMPTS_MIXED: ChatPrompt[] = [...CHAT_PROMPTS].sort(
  (a, b) => stableHash(a.id) - stableHash(b.id)
);
